import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraOverlayDialog, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SurgeryComponentPicker, selectedComponents } from './SurgeryComponentPicker';
import { ReviewAck, SurgeryError, SurgeryFooterActions } from './SurgeryDialogParts';
import { selectionNeedsReview, usePursuitSurgeryPlan, useSplitPursuit } from './surgeryHooks';
/**
 * GRW-19: "Split pursuit…". Opens a sibling pursuit and moves (or links) the
 * chosen components to it; the server claims the new pursuit before it moves
 * anything and is idempotent on the request id, so a retry never opens two.
 */ export function SplitPursuitDialog({ captureProjectId, pursuitName, open, onOpenChange }) {
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "Split pursuit",
        description: `Open a sibling of ${pursuitName} and give it part of this pursuit.`,
        size: "lg",
        scrollBody: true,
        testId: Testids.pursuitSurgery.splitDialog,
        children: open && /*#__PURE__*/ _jsx(SplitBody, {
            captureProjectId: captureProjectId,
            onDone: ()=>onOpenChange(false)
        })
    });
}
function SplitBody({ captureProjectId, onDone }) {
    const navigate = useNavigate();
    const [requestId] = useState(()=>crypto.randomUUID());
    const plan = usePursuitSurgeryPlan(captureProjectId, true);
    const split = useSplitPursuit();
    const [name, setName] = useState('');
    const [reason, setReason] = useState('');
    const [selection, setSelection] = useState({});
    const [ack, setAck] = useState(false);
    const components = selectedComponents(plan.data?.components ?? [], selection);
    const needsReview = selectionNeedsReview(components) && (plan.data?.reviewRequiredIfMovingRfis ?? false);
    const canSubmit = !!name.trim() && !!reason.trim() && (!needsReview || ack) && !split.isPending;
    const submit = ()=>split.mutate({
            sourceCaptureProjectId: captureProjectId,
            newName: name.trim(),
            components,
            reason: reason.trim(),
            requestId,
            proposalReviewAck: ack
        }, {
            onSuccess: (resp)=>{
                onDone();
                navigate(`/capture/${resp.newCaptureProjectId}`);
            }
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "New pursuit name",
                htmlFor: "split-name",
                required: true,
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    id: "split-name",
                    value: name,
                    onChange: (e)=>setName(e.target.value),
                    "data-testid": Testids.pursuitSurgery.nameInput
                })
            }),
            plan.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                variant: "framed",
                children: "The parts of this pursuit could not be loaded."
            }) : /*#__PURE__*/ _jsx(SurgeryComponentPicker, {
                components: plan.data?.components ?? [],
                selection: selection,
                onChange: setSelection
            }),
            needsReview && /*#__PURE__*/ _jsx(ReviewAck, {
                proposals: plan.data?.impactedProposals ?? [],
                verb: "split",
                checked: ack,
                onCheckedChange: setAck
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Reason",
                htmlFor: "split-reason",
                required: true,
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    id: "split-reason",
                    value: reason,
                    onChange: (e)=>setReason(e.target.value),
                    "data-testid": Testids.pursuitSurgery.reasonInput
                })
            }),
            /*#__PURE__*/ _jsx(SurgeryError, {
                error: split.error
            }),
            /*#__PURE__*/ _jsx(SurgeryFooterActions, {
                onCancel: onDone,
                onSubmit: submit,
                disabled: !canSubmit,
                pending: split.isPending,
                label: "Split"
            })
        ]
    });
}
